import { useEffect, useState } from 'react'
import { type PortalChildSummary, type PortalCaregiverSummary } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { Avatar, Box, Button, Container, Typography, Stack } from '@mui/material'
import S3Avatar from 'Components/S3Image/S3Avatar'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DateRangeIcon from '@mui/icons-material/DateRange'
import LockIcon from '@mui/icons-material/Lock'
import { childAge, childName } from 'Pages/CaregiverPortal/PortalHelpers'
import { BirthdayToAgeInYearsAndMonths, FormatLongDate } from 'core/Utilities'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import { ChevronRight } from 'Assets/SVGs/chevron-right'
import { PermissionsSolidIcon } from 'Assets/SVGs/permissions-solid'
import { generatePath, useNavigate } from 'react-router'
import { CAREGIVER_PORTAL_DASHBOARD, CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER, CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD } from 'Pages/Routes'
import { type DuplicateContactInfo } from './DuplicateFound'

const cardListSx = { display: 'flex', flexWrap: 'wrap' as const, gap: '16px' }
const cardSx = {
  backgroundColor: '#ECEDEF',
  borderRadius: '10px',
  p: '16px 20px',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '8px',
  position: 'relative' as const,
  flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' }
}
const infoRowSx = { display: 'flex', alignItems: 'center', gap: '8px' }
const chevronSx = { position: 'absolute' as const, right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#969696' }

// Caregiver card follows Figma "Caregiver Card" (Prototypes, node 8326:2841)
const caregiverCardSx = {
  ...cardSx,
  p: '20px 48px 20px 16px',
  gap: 0
}
const caregiverAvatarStyle = { border: '1px solid #FFFFFF', boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.1)' }
const caregiverInfoRowSx = { display: 'flex', alignItems: 'center', gap: '16px', py: '4px' }
const caregiverChevronSx = { position: 'absolute' as const, right: '16px', top: '50%', transform: 'translateY(-50%)' }

// The member's photo when one is uploaded, otherwise their initial on a primary-color circle
function MemberAvatar (props: { imageKey: string | null, initial: string, style?: React.CSSProperties }): JSX.Element {
  if (props.imageKey != null && props.imageKey !== '') {
    return <S3Avatar imageKey={props.imageKey} alt='' height='32px' width='32px' style={props.style} />
  }
  return <Avatar style={props.style} sx={{ height: '36px', width: '36px', bgcolor: 'primary.main', fontSize: '16px' }}>{props.initial}</Avatar>
}

// Unborn children show "(Unborn)" in place of a first name, so they take its "U"
const memberInitial = (firstName: string | null, lastName: string | null, unborn = false): string => {
  if (unborn) return 'U'
  const name = firstName != null && firstName !== '' ? firstName : lastName ?? ''
  return name.charAt(0).toUpperCase()
}

export function CaregiverMembers (): JSX.Element {
  const nav = useNavigate()
  const [children, setChildren] = useState<PortalChildSummary[]>([])
  const [caregivers, setCaregivers] = useState<PortalCaregiverSummary[]>([])
  const [pendingDupChildren, setPendingDupChildren] = useState<PortalChildSummary[]>([])
  const [duplicateContact, setDuplicateContact] = useState<DuplicateContactInfo | null>()

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet('/CaregiverPortal/GetRelatedMembers', {})
      if (success) {
        setChildren(response.children)
        setCaregivers(response.caregivers)
      }

      const { response: rsp2, success: suc2 } = await sendGet('/CaregiverPortal/GetPendingDuplicates', {})
      if (suc2) {
        setPendingDupChildren(rsp2)

        if (rsp2.length > 0) {
          const { response: isdRsp } = await sendGet('/CaregiverPortal/GetISDSettings?id=', {})
          const isdId: string = isdRsp?.isdid
          const { response: isdDetails } = await sendGet(`/ISDDetails/GetDetails?id=${isdId}`, {})
          const { response: mieccContact } = await sendGet('/SiteConfiguration/GetDuplicateHandlingContact', {})
          if (isdDetails?.duplicateContactName || isdDetails?.duplicateContactEmail) {
            setDuplicateContact({
              name: isdDetails.duplicateContactName,
              email: isdDetails.duplicateContactEmail,
              phone: isdDetails.duplicateContactPhone
            })
          } else if (mieccContact.name || mieccContact.email) {
            setDuplicateContact({
              name: mieccContact.name,
              email: mieccContact.email
            })
          } else {
            setDuplicateContact({ name: isdDetails.name })
          }
        }
      }
    }

    void getData()
  }, [])

  const addChild = (): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD, { childId: 'new' })
    nav(route)
  }

  const addCaregiver = (): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER, { caregiverId: 'new' })
    nav(route)
  }

  const memberCardDate = (child: PortalChildSummary): string => child.unborn ? FormatLongDate(child.dueDate) : FormatLongDate(child.dateOfBirth)

  // Parenthesized to match the caregiver cards; childAge already returns "(Unborn)" for unborn children
  const memberCardAge = (child: PortalChildSummary): string => {
    const age = childAge(child)
    return child.unborn || age === '' ? age : `(${age})`
  }

  const viewChild = (childId: string): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, { childId })
    nav(route)
  }

  const viewCaregiver = (caregiverId: string): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, { caregiverId })
    nav(route)
  }

  return <Container sx={{ mb: '30px' }}>
    <Box sx={{ mt: '30px', display: 'flex', pl: '24px' }}>
      <Box onClick={() => { nav(CAREGIVER_PORTAL_DASHBOARD) }} sx={{ cursor: 'pointer' }}>
        <BackArrow />
      </Box>
      <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px', pl: '24px' }} component='div'>
        Members
      </Typography>
    </Box>

    <Box sx={cardListSx}>
      {caregivers.map((caregiver) => (
        <Box
          key={caregiver.id}
          className='member-card'
          data-testid={`caregiverCard-${caregiver.id}`}
          onClick={() => { viewCaregiver(caregiver.id.toString()) }}
          sx={{ ...caregiverCardSx, cursor: 'pointer' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MemberAvatar imageKey={caregiver.imageKey} initial={memberInitial(caregiver.firstName, caregiver.lastName)} style={caregiverAvatarStyle} />
            <Typography sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.3, color: '#222024' }}>{`${caregiver.firstName} ${caregiver.lastName}`}</Typography>
          </Box>
          {caregiver.hasPortalAccess && (
            <Box sx={caregiverInfoRowSx}>
              <PermissionsSolidIcon />
              <Typography sx={{ fontSize: '14px', color: '#222024' }}>Portal Access</Typography>
            </Box>
          )}
          {caregiver.dateOfBirth != null && (
            <Box sx={caregiverInfoRowSx}>
              <DateRangeIcon sx={{ color: '#222024', fontSize: '24px' }} />
              <Typography sx={{ fontSize: '14px', color: '#222024' }}>{FormatLongDate(caregiver.dateOfBirth)}</Typography>
              <Typography sx={{ fontSize: '12px', lineHeight: 1.3, color: '#514F53' }}>({BirthdayToAgeInYearsAndMonths(caregiver.dateOfBirth, false)})</Typography>
            </Box>
          )}
          <Box sx={caregiverChevronSx}><ChevronRight /></Box>
        </Box>
      ))}
    </Box>

    <Button
      variant='contained'
      className='footer-button'
      sx={{ mt: '20px', py: '16px', fontSize: '16px', fontWeight: 'bold' }}
      name='addCaregiverButton'
      onClick={addCaregiver}
    >
      Add Caregiver
    </Button>

    <Box sx={{ ...cardListSx }}>
      {children.map((child) => (
        <Box
          key={child.id}
          className='member-card'
          data-testid={`childCard-${child.id}`}
          onClick={() => { viewChild(child.id.toString()) }}
          sx={{ ...cardSx, cursor: 'pointer' }}
        >
          <Box sx={infoRowSx}>
            <MemberAvatar imageKey={child.imageKey} initial={memberInitial(child.firstName, child.lastName, child.unborn)} />
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#222024' }}>{childName(child)}</Typography>
          </Box>
          <Box sx={infoRowSx}>
            <DateRangeIcon sx={{ color: '#222024' }} />
            <Typography sx={{ fontSize: '14px', color: '#222024' }}>{memberCardDate(child)}</Typography>
            <Typography sx={{ fontSize: '12px', color: '#514F53' }}>{memberCardAge(child)}</Typography>
          </Box>
          {child.isLocked && (
            <Box sx={infoRowSx}>
              <LockIcon sx={{ color: '#222024' }} fontSize='small' />
              <Typography sx={{ fontSize: '12px', color: '#514F53' }}>A duplicate record is pending review for this child</Typography>
            </Box>
          )}
          <ChevronRightIcon sx={chevronSx} />
        </Box>
      ))}

      {pendingDupChildren.map((child) => (
        <Box
          key={child.id}
          className='member-card'
          data-testid={`pendingDuplicateCard-${child.id}`}
          sx={cardSx}
        >
          <Box sx={infoRowSx}>
            <MemberAvatar imageKey={child.imageKey} initial={memberInitial(child.firstName, child.lastName, child.unborn)} />
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#222024' }}>{`Duplicate Pending Child (${childName(child)})`}</Typography>
          </Box>
          <Stack direction="row">
            <Typography sx={{ fontSize: '14px', color: '#514F53' }}>This child is pending duplicate resolution.</Typography>
          </Stack>
          {duplicateContact !== undefined && (
            <Stack direction="row">
              <Typography sx={{ fontSize: '14px', color: '#514F53' }}>For information on the status of this you can reach out to {duplicateContact?.name} at {duplicateContact?.email}.</Typography>
            </Stack>
          )}
        </Box>
      ))}
    </Box>

    <Button
      variant='contained'
      className='footer-button'
      sx={{ mt: '20px' }}
      name='addChildButton'
      onClick={addChild}
    >
      Add Child
    </Button>
  </Container>
}
